import type { Language } from "./content";

export type MediaItem = {
  id: string;
  src: string;
  poster?: string;
  type: "image" | "video";
  width: number;
  height: number;
  category: "sanctuary" | "community" | "children" | "event" | "video";
  alt: Record<Language, string>;
  caption: Record<Language, string>;
};

const original = (filename: string) => `/media/originals/${filename}`;
const poster = (filename: string) => `/media/posters/${filename}`;

export const mediaItems: MediaItem[] = [
  {
    id: "sanctuary-processional-aisle",
    src: original("sanctuary-processional-aisle.jpg"),
    type: "image",
    width: 960,
    height: 1280,
    category: "sanctuary",
    alt: {
      en: "View down the church’s central aisle toward the carved iconostasis, with embroidered rushnyky draped over the pews",
      uk: "Вигляд центральною навою до різьбленого іконостаса; лави прикрашені вишитими рушниками",
    },
    caption: { en: "The sanctuary prepared for a parish celebration", uk: "Храм, приготовлений до парафіяльного святкування" },
  },
  {
    id: "iconostasis-front",
    src: original("iconostasis-front.jpg"),
    type: "image",
    width: 914,
    height: 1280,
    category: "sanctuary",
    alt: {
      en: "Front view of the ornate carved wooden iconostasis and painted sanctuary arch",
      uk: "Вигляд спереду на оздоблений різьблений іконостас і розписану арку святилища",
    },
    caption: { en: "The iconostasis at the heart of our church", uk: "Іконостас у серці нашого храму" },
  },
  {
    id: "solemn-communion-parish",
    src: original("solemn-communion-parish.jpg"),
    type: "image",
    width: 1280,
    height: 960,
    category: "community",
    alt: {
      en: "Children in formal dress and clergy gathered before the iconostasis for Solemn Holy Communion",
      uk: "Діти у святковому вбранні та духовенство перед іконостасом під час урочистого Святого Причастя",
    },
    caption: { en: "Solemn Holy Communion", uk: "Урочисте Святе Причастя" },
  },
  {
    id: "children-welcome-aisle",
    src: original("children-welcome-aisle.jpg"),
    type: "image",
    width: 1280,
    height: 960,
    category: "children",
    alt: {
      en: "Children in embroidered Ukrainian clothing welcoming parishioners along the church aisle",
      uk: "Діти у вишиванках зустрічають парафіян уздовж церковної нави",
    },
    caption: { en: "Tradition carried with joy", uk: "Традиція, яку несуть із радістю" },
  },
  {
    id: "fr-andrij-portrait",
    src: original("fr-andrij-figol-portrait.jpg"),
    type: "image",
    width: 960,
    height: 1280,
    category: "event",
    alt: {
      en: "Father Andrij Figol with a parishioner at a community celebration",
      uk: "Отець Андрій Фіґоль із парафіянкою на святкуванні громади",
    },
    caption: { en: "Fr. Andrij Figol", uk: "о. Андрій Фіґоль" },
  },
  {
    id: "children-ministry-group",
    src: original("children-ministry-group.jpg"),
    type: "image",
    width: 1280,
    height: 960,
    category: "children",
    alt: {
      en: "Children and teachers in Ukrainian embroidered clothing gathered inside the church",
      uk: "Діти та вчителі в українському вишитому одязі разом у храмі",
    },
    caption: { en: "Learning faith in community", uk: "Пізнаємо віру у спільноті" },
  },
  {
    id: "bishop-and-clergy",
    src: original("bishop-and-clergy.jpg"),
    type: "image",
    width: 960,
    height: 1280,
    category: "community",
    alt: {
      en: "A bishop in green vestments with two priests at the altar after a parish liturgy",
      uk: "Єпископ у зелених ризах із двома священниками біля престолу після парафіяльної Літургії",
    },
    caption: { en: "A visit shared in prayer", uk: "Візит, поєднаний молитвою" },
  },
  {
    id: "children-performance",
    src: original("children-performance.jpg"),
    type: "image",
    width: 1280,
    height: 960,
    category: "children",
    alt: {
      en: "Children in Ukrainian embroidered clothing lined up to sing before the iconostasis",
      uk: "Діти у вишиванках вишикувалися для співу перед іконостасом",
    },
    caption: { en: "Young voices in our parish", uk: "Молоді голоси нашої парафії" },
  },
  {
    id: "choir-and-fr-andrij",
    src: original("choir-and-fr-andrij.jpg"),
    type: "image",
    width: 960,
    height: 1280,
    category: "children",
    alt: {
      en: "Children’s choir, adult leaders, and Father Andrij gathered beside the iconostasis",
      uk: "Дитячий хор, дорослі керівники та отець Андрій біля іконостаса",
    },
    caption: { en: "Children’s choir with Fr. Andrij", uk: "Дитячий хор з о. Андрієм" },
  },
  {
    id: "blessing-of-children",
    src: original("blessing-of-children.jpg"),
    type: "image",
    width: 960,
    height: 1280,
    category: "children",
    alt: {
      en: "Father Andrij in blue vestments blessing children inside the sanctuary",
      uk: "Отець Андрій у блакитних ризах благословляє дітей у храмі",
    },
    caption: { en: "A blessing for our children", uk: "Благословення для наших дітей" },
  },
  {
    id: "christmas-carollers-church",
    src: original("christmas-carollers-church.jpg"),
    type: "image",
    width: 1280,
    height: 960,
    category: "community",
    alt: {
      en: "Parish Christmas carollers in traditional clothing gathered around a blue and gold star",
      uk: "Парафіяльні колядники у традиційному вбранні з синьо-золотою різдвяною зіркою",
    },
    caption: { en: "Christmas carolling together", uk: "Колядуємо разом" },
  },
  {
    id: "christmas-carollers-home",
    src: original("christmas-carollers-home.jpg"),
    type: "image",
    width: 1280,
    height: 960,
    category: "community",
    alt: {
      en: "Adults and children visiting a home together for Ukrainian Christmas carolling",
      uk: "Дорослі й діти разом завітали до оселі з українською колядою",
    },
    caption: { en: "Bringing the carol from home to home", uk: "Несемо коляду від хати до хати" },
  },
  {
    id: "st-nicholas-children",
    src: original("st-nicholas-children.jpg"),
    type: "image",
    width: 1280,
    height: 960,
    category: "children",
    alt: {
      en: "Children in Ukrainian embroidered clothing gathered with St. Nicholas beside a Christmas tree",
      uk: "Діти у вишиванках зі святим Миколаєм біля різдвяної ялинки",
    },
    caption: { en: "St. Nicholas visits the children", uk: "Святий Миколай завітав до дітей" },
  },
  {
    id: "church-exterior",
    src: original("church-exterior.jpg"),
    type: "image",
    width: 591,
    height: 1280,
    category: "sanctuary",
    alt: {
      en: "Street-level view of the red-brick church entrance, crucifix, steps, and red awning",
      uk: "Вигляд із вулиці на вхід до церкви з червоної цегли, розп’яття, сходи та червоний дашок",
    },
    caption: { en: "Our church on Sixth Street", uk: "Наша церква на Sixth Street" },
  },
  {
    id: "instagram-feast-decoration",
    src: original("instagram-feast-decoration.jpg"),
    type: "image",
    width: 1280,
    height: 960,
    category: "event",
    alt: {
      en: "Icon of Saint Michael surrounded by blessed fruit, sunflowers, and orchids before the church iconostasis",
      uk: "Ікона святого Михаїла серед освячених плодів, соняхів та орхідей перед церковним іконостасом",
    },
    caption: { en: "Feast of the Transfiguration", uk: "Преображення Господнє" },
  },
  {
    id: "anniversary-flyer",
    src: original("fr-andrij-25th-anniversary-flyer.jpg"),
    type: "image",
    width: 997,
    height: 1280,
    category: "event",
    alt: {
      en: "Bilingual event flyer for Father Andrij Figol’s 25th anniversary of ordination on October 3, 2026",
      uk: "Двомовна афіша святкування 25-ліття священничих свячень отця Андрія Фіґоля 3 жовтня 2026 року",
    },
    caption: { en: "Anniversary invitation", uk: "Запрошення на ювілей" },
  },
  {
    id: "st-michael-feast-decoration",
    src: original("st-michael-feast-decoration.mp4"),
    poster: poster("st-michael-feast-decoration.jpg"),
    type: "video",
    width: 720,
    height: 1280,
    category: "video",
    alt: {
      en: "Video of the St. Michael icon and sanctuary decorated with sunflowers and flowers",
      uk: "Відео ікони святого Михаїла та святилища, прикрашеного соняхами й квітами",
    },
    caption: { en: "St. Michael feast-day decoration", uk: "Оздоблення до свята святого Михаїла" },
  },
  {
    id: "fruit-blessing",
    src: original("fruit-blessing.mp4"),
    poster: poster("fruit-blessing.jpg"),
    type: "video",
    width: 720,
    height: 1280,
    category: "video",
    alt: {
      en: "Video of children and parishioners gathering around fruit and flowers for a blessing",
      uk: "Відео дітей і парафіян, які збираються біля плодів і квітів для благословення",
    },
    caption: { en: "Blessing of fruit with the parish", uk: "Парафіяльне благословення плодів" },
  },
  {
    id: "children-singing",
    src: original("children-singing.mp4"),
    poster: poster("children-singing.jpg"),
    type: "video",
    width: 1280,
    height: 720,
    category: "video",
    alt: {
      en: "Video of children singing in embroidered clothing before the iconostasis",
      uk: "Відео дітей у вишиванках, які співають перед іконостасом",
    },
    caption: { en: "Our children sing", uk: "Співають наші діти" },
  },
];

function currentLanguage(): Language {
  return document.documentElement.lang === "uk" ? "uk" : "en";
}

export function renderGallery(): void {
  const gallery = document.querySelector<HTMLElement>("#gallery-grid");
  if (!gallery) return;
  const language = currentLanguage();

  gallery.innerHTML = mediaItems
    .map((item, index) => {
      const media = item.type === "video"
        ? `<img src="${item.poster}" alt="${item.alt[language]}" width="${item.width}" height="${item.height}" loading="lazy">`
        : `<img src="${item.src}" alt="${item.alt[language]}" width="${item.width}" height="${item.height}" loading="lazy">`;
      const mediaLabel = item.type === "video" ? (language === "uk" ? "Відео" : "Video") : (language === "uk" ? "Світлина" : "Photograph");
      const actionLabel = item.type === "video" ? (language === "uk" ? "Відтворити відео" : "Play video") : (language === "uk" ? "Відкрити світлину" : "Open photograph");
      return `
        <button class="gallery-item gallery-item--${item.type} gallery-item--${item.id}" type="button" data-media-index="${index}" aria-label="${actionLabel}: ${item.caption[language]}">
          <span class="gallery-item__media">${media}</span>
          <span class="gallery-item__caption">
            <span>${item.caption[language]}</span>
            <small>${mediaLabel}</small>
          </span>
        </button>`;
    })
    .join("");
}
